import { CollectionModel, CollectionModelFieldSubFormInstance, FormModelFieldSubForm } from '@ikegps/core-shared';
import { ModalFormComponent, type ModalFormComponentCtx } from '../../components/modal.form/modal.form.component';
import { ModalService } from '../../components/modal/modal.service';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { CSS as CollectionViewCSS } from '../collection.view/collection.view.style';
import { CollectionEditViewSubform } from '../collection.view/collection.view.subform';
import { CSS, Style } from './span.link.modal.style';
import { SpanLinkModalState, type SpanListItem } from './span.link.modal.state';
import { ReadonlyCollectionViewCtrl } from './readonly.collection.view.ctrl';

addStyles(Style);

export interface SpanLinkModalResult {
	selectedSpanId: string | null;
	createNew: boolean;
	cancelled: boolean;
}

/**
 * Modal for selecting a span to link to on the target collection.
 * Shows a split panel with span list on the left and preview on the right.
 */
export class SpanLinkModal extends ModalFormComponent {
	private state: SpanLinkModalState;
	private readonlyCtrl: ReadonlyCollectionViewCtrl;

	constructor(ctx: ModalFormComponentCtx, state: SpanLinkModalState) {
		super(ctx);
		this.state = state;
		this.readonlyCtrl = new ReadonlyCollectionViewCtrl();

		// Disable submit button when nothing selected
		this.autorun(() => {
			this.isSubmitButtonDisabled = !this.state.canSubmit;
		});
	}

	get nodes(): MithrilVNode {
		return m('div', { className: CSS.container }, [this.viewLeftPanel(), this.viewRightPanel()]);
	}

	private viewLeftPanel(): MithrilVNode {
		return m('div', { className: CSS.leftPanel }, [
			m('div', { className: CSS.panelTitle }, Translate(T.SPAN_LINK_SELECT_SPAN_TITLE)),
			this.viewSpanList(),
			m('div', { className: CSS.panelFooter }, [
				m(
					'a',
					{
						href: 'https://support.ikegps.com/hc/en-us/articles/44077734089229-Span-Linking-in-IKE-Office-Pro',
						target: '_blank',
					},
					Translate(T.SPAN_LINK_LEARN_MORE)
				),
			]),
		]);
	}

	private viewCreateNewOption(): MithrilVNode | null {
		// Don't show create new if there's an existing link to source
		if (this.state.hasExistingLinkToSource) {
			return null;
		}

		const isSelected = this.state.createNewSpan;
		const inputId = 'span-link-create-new';

		return m('div', { className: CSS.createNewOption }, [
			m('input', {
				type: 'radio',
				id: inputId,
				name: 'span-selection',
				checked: isSelected ? 'checked' : null,
				onchange: () => this.state.selectCreateNew(),
			}),
			m('label', { for: inputId }, '+ ' + Translate(T.SPAN_LINK_CREATE_NEW)),
		]);
	}

	private viewSpanList(): MithrilVNode {
		const spans = this.state.availableSpans;

		return m('div', { className: CSS.spanList }, [
			...spans.map((item) => this.viewSpanItem(item)),
			this.viewCreateNewOption(),
		]);
	}

	private viewSpanItem(item: SpanListItem): MithrilVNode {
		const isSelected = this.state.selectedSpanId === item.instanceId;
		const inputId = `span-link-item-${item.instanceId}`;

		// Use the span color for the left border, default to transparent if no color
		const borderColor = item.color || 'transparent';

		return m('div', { className: CSS.spanItem }, [
			m('input', {
				type: 'radio',
				id: inputId,
				name: 'span-selection',
				checked: isSelected ? 'checked' : null,
				onchange: () => this.state.selectSpan(item.instanceId),
			}),
			m(
				'label',
				{
					for: inputId,
					style: { 'border-left-color': borderColor },
				},
				item.title + (item.spanType ? ` (${item.spanType})` : '')
			),
		]);
	}

	private viewRightPanel(): MithrilVNode {
		const selectedSpan = this.state.selectedSpan;

		if (!selectedSpan && !this.state.createNewSpan) {
			return m('div', { className: CSS.rightPanel }, [
				m('div', { className: CSS.previewEmpty }, Translate(T.SPAN_LINK_SELECT_SPAN)),
			]);
		}

		if (this.state.createNewSpan) {
			return m('div', { className: CSS.rightPanel }, [
				m('div', { className: CSS.previewEmpty }, Translate(T.SPAN_LINK_NEW_SPAN_INFO)),
			]);
		}

		const instance = this.state.selectedSpanInstance;
		if (!instance) {
			return m('div', { className: CSS.rightPanel }, [m('div', { className: CSS.previewEmpty }, 'Error loading span')]);
		}

		return m('div', { className: CSS.rightPanel }, [
			m('div', { className: CSS.infoText }, Translate(T.SPAN_LINK_WARNING_TEXT)),
			m('div', { className: CSS.previewContainer }, this.viewSpanInstance(instance)),
		]);
	}

	/**
	 * Renders the span instance with header (matching CollectionEditViewSubform.viewInstance pattern)
	 */
	private viewSpanInstance(instance: CollectionModelFieldSubFormInstance): MithrilVNode {
		// Build the title like CollectionEditViewSubform.viewInstance does
		const instanceTitle = instance.title;
		const formTitle = instance.formTitle;
		let title = formTitle;
		if (instanceTitle != null && instanceTitle !== formTitle) {
			title = `${formTitle} — ${instanceTitle}`;
		}

		// Get span color (matches CollectionEditViewSubform behavior)
		const spanColor = this.readonlyCtrl.useColorCoding ? FormModelFieldSubForm.getSpanColor(instance) : null;
		const borderColor = spanColor || 'transparent';

		return m('div', { className: CollectionViewCSS.instance.container }, [
			// Header with span color border and title
			m(
				'div',
				{
					title,
					className: CollectionViewCSS.instance.header,
					style: { 'border-left': `6px solid ${borderColor}`, cursor: 'default' },
				},
				[m('div', { className: CollectionViewCSS.instance.text }, title)]
			),
			// Fields rendered in readonly mode
			CollectionEditViewSubform.view(this.readonlyCtrl, instance),
		]);
	}

	override validate() {
		// Validation is handled by canSubmit computed via isSubmitButtonDisabled
	}

	override async submit(): Promise<void> {
		// The actual result is returned via openSpanLinkModal promise
	}

	override close() {
		this.state.reset();
		super.close();
	}
}

/**
 * Opens the span link modal and returns a promise with the user's selection.
 *
 * @param sourceCollection The collection initiating the link
 * @param targetCollection The target collection to link to
 * @param sourceSpanInstanceId The ID of the span instance on the source collection
 * @returns Promise that resolves with the modal result
 */
export async function openSpanLinkModal(
	sourceCollection: CollectionModel,
	targetCollection: CollectionModel,
	sourceSpanInstanceId: string
): Promise<SpanLinkModalResult> {
	return new Promise((resolve) => {
		const state = new SpanLinkModalState();
		state.initialize(sourceCollection, targetCollection, sourceSpanInstanceId);

		const modal = new SpanLinkModal(
			{
				title: T.SPAN_LINK_MODAL_TITLE,
				confirm: T.SPAN_LINK_CONFIRM,
				success: { key: T.SPAN_LINK_SUCCESS },
			},
			state
		);

		// We need to intercept the modal closing to capture the result
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		ModalService.open({
			title: T.SPAN_LINK_MODAL_TITLE,
			content: () => modal.nodes,
			actions: () =>
				m.fragment({}, [
					m(
						'button',
						{
							className: 'mdl-button',
							onclick: () => {
								state.reset();
								ModalService.close(false);
								resolve({ selectedSpanId: null, createNew: false, cancelled: true });
							},
							type: 'button',
						},
						Translate(T.CANCEL)
					),
					m(
						'button',
						{
							className: 'mdl-button mdl-button--accent',
							disabled: !state.canSubmit,
							onclick: () => {
								const result = {
									selectedSpanId: state.selectedSpanId,
									createNew: state.createNewSpan,
									cancelled: false,
								};
								state.reset();
								ModalService.close(true);
								resolve(result);
							},
							type: 'submit',
						},
						Translate(T.SPAN_LINK_CONFIRM)
					),
				]),
			canClose: () => true,
		});
	});
}
